import { Avatar } from '@/components/ui';
import type { UserSummary } from '@/types/api';
import { useEffect, useMemo, useRef, useState } from 'react';

/** Persona mencionable: hace falta el usuario, que es lo que se escribe tras la @. */
export interface MentionablePerson extends UserSummary {
  username: string;
}

/** Token de mencion en curso: la @ y lo tecleado despues, hasta el cursor. */
interface ActiveMention {
  query: string;
  /** Indice de la @ dentro del texto. */
  start: number;
}

/**
 * Detecta si el cursor esta escribiendo una mencion.
 *
 * Se exige que la @ abra palabra (inicio del texto o espacio delante) para no
 * confundirla con la de un correo: en "ana@synq.com" la arroba no es mencion.
 */
function activeMention(value: string, caret: number): ActiveMention | null {
  const match = /(?:^|\s)@([\p{L}\p{N}._-]*)$/u.exec(value.slice(0, caret));
  if (!match) {
    return null;
  }
  return { query: match[1], start: caret - match[1].length - 1 };
}

/**
 * Campo de texto con autocompletado de menciones.
 *
 * Las candidatas las decide quien lo usa: en una tarea son los miembros del
 * proyecto, porque mencionar a alguien que no puede verla no sirve de nada.
 */
export function MentionInput({
  value,
  onChange,
  onSubmit,
  people,
  placeholder,
  ariaLabel,
  disabled = false,
  className = '',
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  people: MentionablePerson[];
  placeholder?: string;
  ariaLabel: string;
  disabled?: boolean;
  className?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [mention, setMention] = useState<ActiveMention | null>(null);
  const [highlighted, setHighlighted] = useState(0);

  const matches = useMemo(() => {
    if (!mention) {
      return [];
    }
    const query = mention.query.toLowerCase();
    return people
      .filter(
        (person) =>
          !query ||
          person.username.toLowerCase().includes(query) ||
          person.fullName.toLowerCase().includes(query),
      )
      .slice(0, 6);
  }, [mention, people]);

  // Si cambian las coincidencias, la seleccion vuelve a la primera para no
  // quedar apuntando a una fila que ya no esta.
  useEffect(() => {
    setHighlighted(0);
  }, [mention?.query]);

  const open = mention !== null && matches.length > 0;

  function refreshMention(target: HTMLInputElement) {
    setMention(activeMention(target.value, target.selectionStart ?? target.value.length));
  }

  function insert(person: MentionablePerson) {
    const input = inputRef.current;
    if (!input || !mention) {
      return;
    }
    const caret = input.selectionStart ?? value.length;
    const before = value.slice(0, mention.start);
    const after = value.slice(caret);
    const inserted = `@${person.username} `;

    onChange(before + inserted + after);
    setMention(null);

    // El cursor queda tras la mencion, listo para seguir escribiendo.
    const nextCaret = before.length + inserted.length;
    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(nextCaret, nextCaret);
    });
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (open) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlighted((i) => (i + 1) % matches.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlighted((i) => (i - 1 + matches.length) % matches.length);
        return;
      }
      // Enter y Tab eligen; sin esto Enter enviaria el comentario a medias.
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        insert(matches[highlighted]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setMention(null);
        return;
      }
    }

    if (event.key === 'Enter' && onSubmit) {
      event.preventDefault();
      onSubmit();
    }
  }

  return (
    <div className="relative flex-1">
      {open && (
        <ul
          role="listbox"
          aria-label="Personas para mencionar"
          className="absolute bottom-full left-0 z-20 mb-1.5 w-[280px] overflow-hidden rounded-control border border-line bg-surface py-1 shadow-[0_16px_40px_rgba(0,0,0,.55)]"
        >
          {matches.map((person, index) => (
            <li key={person.id}>
              <button
                type="button"
                role="option"
                aria-selected={index === highlighted}
                // El raton no puede robar el foco antes de insertar.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlighted(index)}
                onClick={() => insert(person)}
                className={`flex w-full items-center gap-2.5 px-2.5 py-1.5 text-left transition-colors ${
                  index === highlighted ? 'bg-brand-tint' : ''
                }`}
              >
                <Avatar user={person} size={24} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium">{person.fullName}</span>
                  <span className="block truncate font-mono text-[11px] text-faint">
                    @{person.username}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={inputRef}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={open}
        role="combobox"
        onChange={(e) => {
          onChange(e.target.value);
          refreshMention(e.target);
        }}
        // Mover el cursor tambien cambia si hay mencion en curso.
        onKeyUp={(e) => refreshMention(e.currentTarget)}
        onClick={(e) => refreshMention(e.currentTarget)}
        onBlur={() => setMention(null)}
        onKeyDown={handleKeyDown}
        className={className}
      />
    </div>
  );
}

/**
 * Pinta un texto resaltando las menciones que corresponden a alguien real.
 *
 * Se comprueba contra la lista de personas a proposito: si no, un correo o un
 * "@algo" cualquiera se veria como mencion sin serlo.
 */
export function renderWithMentions(text: string, people: MentionablePerson[]): React.ReactNode[] {
  const usernames = new Set(people.map((p) => p.username.toLowerCase()));
  const parts = text.split(/(?:^|(?<=\s))(@[\p{L}\p{N}._-]+)/u);

  return parts.map((part, index) => {
    if (part.startsWith('@') && usernames.has(part.slice(1).toLowerCase())) {
      return (
        <strong key={index} className="rounded bg-brand-tint px-1 font-semibold text-brand-deep">
          {part}
        </strong>
      );
    }
    return <span key={index}>{part}</span>;
  });
}
